import React, { useState, useEffect, useRef } from 'react';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { useTranslation } from 'react-i18next';
import { MdMyLocation, MdOutlineWarningAmber } from 'react-icons/md';

export default function MapPositionInteractive(props: {
    onCenterChange?: (pos: { lat: number; lng: number }) => void;
    onSetZoom?: (zoom: number) => void;
    selectedCoordinates?: { lat: number; lng: number } | undefined;
    onReport: () => void;
}) {
    const { t } = useTranslation();
    const [autocompleteService, setAutocompleteService] = useState<any>();
    const [predictions, setPredictions] = useState<any[] | null>(null);
    const placesLib = useMapsLibrary('places');
    const geocodingLib = useMapsLibrary('geocoding');
    const inputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (!placesLib) return;
        // placesLib exposes the constructors directly (as returned by google.maps.importLibrary)
        setAutocompleteService(new placesLib.AutocompleteService());
    }, [placesLib]);

    function selectPrediction(prediction: any) {
        if (!geocodingLib) return;
        const geocoder = new geocodingLib.Geocoder();
        geocoder.geocode({ placeId: prediction.place_id }, (results: any, status: any) => {
            if (status === 'OK' && results?.[0]?.geometry?.location) {
                const location = results[0].geometry.location.toJSON();
                // notify parent to center map
                props.onCenterChange?.(location);
                // adjust zoom heuristically
                if (prediction.types?.includes('locality')) props.onSetZoom?.(13);
                else if (prediction.types?.includes('country')) props.onSetZoom?.(6);
                else if (prediction.types?.includes('route')) props.onSetZoom?.(18);
                else if (prediction.types?.includes('premise')) props.onSetZoom?.(20);
            }
        });
        setPredictions(null);
        if (inputRef.current) inputRef.current.value = prediction.description;
    }

    function getMyLocalization(position: GeolocationPosition) {
        const loc = { lat: position.coords.latitude, lng: position.coords.longitude };
        props.onCenterChange?.(loc);
        props.onSetZoom?.(20);
    }

    return (
        <div
            className={
                'absolute z-[1] mt-3 ml-4 flex flex-col items-start gap-2 sm:flex-row sm:items-center'
            }>
            <div className={'relative'}>
                <input
                    ref={inputRef}
                    type="text"
                    placeholder={t('MAP.SEARCH_FOR_LOCATION') as string}
                    className={'rounded py-2 px-3 outline-0'}
                    onChange={(e) => {
                        const value = e.target.value;
                        if (!value || !autocompleteService) {
                            setPredictions(null);
                            return;
                        }
                        autocompleteService.getPlacePredictions(
                            { input: value, componentRestrictions: { country: 'pl' } },
                            (preds: any) => {
                                setPredictions(preds || null);
                            },
                        );
                    }}
                />

                {predictions && predictions.length > 0 && (
                    <div className={'absolute z-10 mt-1 w-full rounded bg-white shadow'}>
                        {predictions.map((p) => (
                            <div
                                key={p.place_id}
                                className={'cursor-pointer px-3 py-2 hover:bg-gray-100'}
                                onClick={() => selectPrediction(p)}>
                                {p.description}
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <div className={'flex gap-2'}>
                <button
                    className={
                        'flex rounded bg-SM-yellow p-2 text-white ring-SM-yellow transition-all hover:text-black hover:ring-2'
                    }
                    onClick={() => {
                        navigator.geolocation?.getCurrentPosition(getMyLocalization);
                    }}>
                    <MdMyLocation className={'text-2xl'} />
                </button>
                <button
                    onClick={props.onReport}
                    className={
                        'flex items-center gap-2 rounded bg-SM-yellow p-2 text-white ring-SM-yellow transition-all enabled:hover:text-black enabled:hover:ring-2 disabled:bg-gray-300'
                    }
                    disabled={!props.selectedCoordinates}>
                    <MdOutlineWarningAmber className={'text-2xl'} />
                    {t('translation:MAP.REPORT')}
                </button>
            </div>
        </div>
    );
}
